import { useMemo, useState } from 'react';
import classNames from 'classnames';
import { Col, Row } from 'react-bootstrap';
import { IconArrowUpDown, IconInfoCircle } from '@medifind/icons';
import { toSlug } from '@medifind/router';
import { Button, MFLink, ScoreLabel, Skeleton, What } from '@medifind/shared-basic-components';
import { getNullList, scrollToId } from '@medifind/utils';
import {
  clearConditionFilterOption,
  logEventGA4,
  selectConditionFilter,
  updateConditionFilterOption,
  updatePublicationRecentFilterOption,
  updatePublicationTypeFilterOption,
  useConditionFilter,
} from '@medifind/zustand';
import styles from './AreasOfExpertise.module.scss';

const TooltipList = ({ tierNumber, tiers = [] }) => {
  const tier = tiers.find((t) => t.barNumber === tierNumber);
  return (
    <ul className={styles['tier-ul']}>
      {tier?.items?.map((item, i) => (
        <li key={i}>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
};

const tiers = [
  {
    name: 'Elite',
    color: '#E5243E',
    barNumber: 4,
    items: [
      'Global leaders in their field',
      'Publish in medical journals frequently',
      'Speak at conferences',
      'Participate in clinical trials',
      'Likely to work with cutting edge treatments',
      'Likely to hold leadership positions in their organization',
    ],
  },
  {
    name: 'Distinguished',
    color: '#810196',
    barNumber: 3,
    items: [
      'Leaders in their field',
      'Strong research portfolio',
      'Likely to work with cutting edge treatments',
      'Likely to be a leader in their field regionally',
    ],
  },
  {
    name: 'Advanced',
    color: '#022172',
    barNumber: 2,
    items: [
      'Excellent at diagnosing and treating conditions',
      'Treat many patients and have extensive experience in their field',
      'May not publish, speak at conferences, or participate in clinical trials',
    ],
  },
  {
    name: 'Experienced',
    color: '#4062BB',
    barNumber: 1,
    items: [
      'Primarily treat patients rather than focus on research',
      'May be specialists or primary care physicians',
      'Have great experience in diagnosing and treating conditions',
      'Majority of doctors fall within this level of expertise',
    ],
  },
];

const tierLabelMap = tiers.reduce((acc, tier) => {
  acc[tier.barNumber] = tier.name;
  return acc;
}, {});

const ViewMoreText = ({ showAll, viewMoreWhat, tierLabel, count }) => {
  if (showAll) {
    return <>View Less&nbsp;</>;
  }
  return (
    <>
      View All {count} {tierLabel} {viewMoreWhat ? viewMoreWhat : 'Conditions'}{' '}
    </>
  );
};

const TierSection = ({ tierConditions = [], tierNumber, classes = {}, highlightSelected, viewMoreWhat = '' }) => {
  const [showAll, setShowAll] = useState(false);
  const conditionFilter = useConditionFilter(selectConditionFilter);
  const { condition } = conditionFilter || {};
  const { value: selectedProjectId } = condition?.selected || {};

  const tierStyle = {
    [styles[`tier-1`]]: tierConditions[0]?.tier === 1,
    [styles[`tier-2`]]: tierConditions[0]?.tier === 2,
    [styles[`tier-3`]]: tierConditions[0]?.tier === 3,
    [styles[`tier-4`]]: tierConditions[0]?.tier === 4,
  };

  const conditionsToDisplay = showAll ? tierConditions : tierConditions.slice(0, 6);
  return (
    <>
      <ul
        className={classNames(styles['list'], classes?.list, {
          [styles[`list--section-seperater`]]: highlightSelected,
          tierStyle,
        })}
      >
        {tierConditions ? (
          <>
            {tierConditions?.[0] ? (
              <>
                <li className={classNames(styles['flex-row'])}>
                  <ScoreLabel score={tierConditions[0].score} classes={{ label: classes?.label }} />
                  <What
                    icon={<IconInfoCircle />}
                    hideHeading
                    classes={{
                      tooltipContainer: [styles['tooltip-container'], tierStyle],
                    }}
                  >
                    <p className={styles['tooltip-description--bold']}>
                      What does{' '}
                      <span className={classNames(styles['tier-name'], tierStyle)}>{tierLabelMap[tierNumber]?.toUpperCase()}</span> mean?
                    </p>
                    <p className={styles['tooltip-description']}>Meets the following criteria per specific condition:</p>
                    <TooltipList tierNumber={tierNumber} tiers={tiers} />
                  </What>
                </li>
              </>
            ) : null}
          </>
        ) : (
          <Skeleton height={41} style={{ margin: '1rem 0' }} />
        )}

        {(conditionsToDisplay || getNullList(6)).map((area, i) => {
          if (area) {
            const { projectId, codingName, score } = area;
            const slug = toSlug(codingName);
            return (
              <li
                className={classNames(styles['item'], classes?.item, {
                  [styles['item--selected']]: highlightSelected && projectId == selectedProjectId,
                })}
                key={'areas-of-expertise-' + i}
              >
                <MFLink
                  to={!highlightSelected ? `/conditions/${slug}/${projectId}` : null}
                  onClick={() => {
                    if (highlightSelected) {
                      logEventGA4('therapeutic_filter', {
                        action: 'select',
                        item_id: projectId,
                        condition: codingName,
                      });
                      updateConditionFilterOption({ display: codingName, value: projectId });
                      updatePublicationTypeFilterOption({ value: '', display: 'All Types' });
                      updatePublicationRecentFilterOption({ value: false });
                      scrollToId('personHeader');
                    } else {
                      return false;
                    }
                  }}
                  className={classNames(styles['area'], {
                    [styles['area--selected']]: highlightSelected && projectId == selectedProjectId,
                    [styles['area--link']]: highlightSelected,
                  })}
                >
                  {codingName}
                </MFLink>
              </li>
            );
          } else {
            return (
              <li key={'areas-of-expertise-' + i}>
                <Skeleton />
              </li>
            );
          }
        })}
      </ul>
      {tierConditions?.length > 6 && (
        <div
          className={classNames({
            [styles['view-more']]: highlightSelected,
          })}
        >
          <div style={{ textAlign: 'center', margin: '0.5rem 0' }}>
            <MFLink className={styles['link']} onClick={() => setShowAll(!showAll)}>
              <ViewMoreText
                showAll={showAll}
                viewMoreWhat={viewMoreWhat}
                tierLabel={tierLabelMap[tierNumber]}
                count={tierConditions.length}
              />
              <IconArrowUpDown direction={showAll ? 'up' : 'down'} />
            </MFLink>
          </div>
        </div>
      )}
      {(!conditionsToDisplay || conditionsToDisplay.length > 0) && <hr className={classes.hr} />}
    </>
  );
};

const AreasOfExpertise = ({ areasOfExpertise = [], classes, highlightSelected, viewMoreWhat = '' }) => {
  const conditionFilter = useConditionFilter(selectConditionFilter);
  const { condition } = conditionFilter || {};
  const { value: selectedProjectId } = condition?.selected || {};

  const { tier4, tier3, tier2, tier1 } = useMemo(() => {
    if (!areasOfExpertise) return {};
    const allExpertiseData = areasOfExpertise
      .filter((condition) => {
        // do not show null scores or scores below 5
        return condition?.score != null && condition?.score >= 5;
      })
      .map((expertise) => {
        const tier = Math.min(Math.floor(expertise.score / 25) + 1, 4);
        expertise.tier = tier;
        return expertise;
      });
    const sortedData = allExpertiseData.sort((a, b) =>
      a.tier === b.tier ? a.codingName.localeCompare(b.codingName) : a.tier < b.tier ? 1 : -1,
    );
    return {
      tier4: sortedData.filter(({ tier }) => tier === 4),
      tier3: sortedData.filter(({ tier }) => tier === 3),
      tier2: sortedData.filter(({ tier }) => tier === 2),
      tier1: sortedData.filter(({ tier }) => tier === 1),
    };
  }, [areasOfExpertise]);

  /* if (!areasOfExpertise) {
    return <Skeleton count={initialCount} />;
  } */

  return (
    <div className={classNames(styles['areas-of-expertise'])}>
      {areasOfExpertise?.length === 0 ? (
        <Row>
          <Col>
            <div>
              <p id="no-expertise-data">We do not have enough information to identify the Areas of Expertise for this provider.</p>
            </div>
          </Col>
        </Row>
      ) : (
        <>
          <TierSection
            tierNumber={4}
            tierConditions={tier4}
            classes={classes}
            highlightSelected={highlightSelected}
            viewMoreWhat={viewMoreWhat}
          />
          <TierSection
            tierNumber={3}
            tierConditions={tier3}
            classes={classes}
            highlightSelected={highlightSelected}
            viewMoreWhat={viewMoreWhat}
          />
          <TierSection
            tierNumber={2}
            tierConditions={tier2}
            classes={classes}
            highlightSelected={highlightSelected}
            viewMoreWhat={viewMoreWhat}
          />
          <TierSection
            tierNumber={1}
            tierConditions={tier1}
            classes={classes}
            highlightSelected={highlightSelected}
            viewMoreWhat={viewMoreWhat}
          />
        </>
      )}

      {highlightSelected && selectedProjectId && (
        <div className={styles['clear-button']}>
          <Button color="light-blue" type="thin" label="Clear Filter" onClick={() => clearConditionFilterOption()} />
        </div>
      )}
    </div>
  );
};

export { AreasOfExpertise as default, AreasOfExpertise };
